import { useCallback, useEffect, useState } from 'react';

type InitialValue<T> = T | (() => T);

export enum WebStorageType {
  Local = 'local',
  Session = 'session'
}

export interface UseWebStorageOptions<T> {
  serialize?: (value: T) => string;
  deserialize?: (value: string) => T;
}

export const canUseWebStorage = (type: WebStorageType): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const storage =
      type === WebStorageType.Local
        ? window.localStorage
        : window.sessionStorage;

    return !!storage;
  } catch {
    return false;
  }
};

const defaultSerialize = <T>(value: T): string => JSON.stringify(value);

const defaultDeserialize = <T>(value: string, fallback: T): T => {
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

const getInitialValue = <T>(initialValue: InitialValue<T>): T => {
  return typeof initialValue === 'function'
    ? (initialValue as () => T)()
    : initialValue;
};

const getStorage = (type: WebStorageType): Storage | null => {
  if (!canUseWebStorage(type)) {
    return null;
  }
  return type === WebStorageType.Local
    ? window.localStorage
    : window.sessionStorage;
};

/**
 * React hook to read/write a value in `window.localStorage` or `window.sessionStorage`.
 *
 * - SSR-safe (falls back to initial value when `window` is unavailable)
 * - JSON serialize/deserialize by default (customizable via options)
 * - Supports functional updates like `setValue((prev: T) => T)`
 */
const useWebStorage = <T>(
  type: WebStorageType,
  key: string,
  initialValue: InitialValue<T>,
  options: UseWebStorageOptions<T> = {}
): readonly [T, (value: T | ((prev: T) => T)) => void, () => void] => {
  const initial = getInitialValue(initialValue);
  const { serialize = defaultSerialize, deserialize } = options;

  const readValue = useCallback((): T => {
    const storage = getStorage(type);
    if (!storage) {
      return initial;
    }

    const storedValue = storage.getItem(key);
    if (storedValue === null) {
      return initial;
    }

    if (deserialize) {
      try {
        return deserialize(storedValue);
      } catch {
        return initial;
      }
    }

    return defaultDeserialize(storedValue, initial);
  }, [deserialize, initial, key, type]);

  const [value, setValueState] = useState<T>(() => readValue());

  // Keep state in sync if the key or storage type changes.
  useEffect(() => {
    setValueState(readValue());
  }, [readValue]);

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValueState((prev: T) => {
        const resolved = next instanceof Function ? next(prev) : next;
        const storage = getStorage(type);

        if (storage) {
          storage.setItem(key, serialize(resolved));
        }

        return resolved;
      });
    },
    [key, serialize, type]
  );

  const remove = useCallback(() => {
    const storage = getStorage(type);

    if (storage) {
      storage.removeItem(key);
    }
    setValueState(initial);
  }, [initial, key, type]);

  return [value, setValue, remove] as const;
};

export default useWebStorage;
